import _ from 'lodash';
import React, {FunctionComponent, ReactNode, useEffect, useState} from 'react';
import {ProductInCatalog, ProductData} from '@treez/commons/sharedTypings/product';
import {Grid, makeStyles} from '@material-ui/core';
import {ColumnWidthOption} from '~/lib/sharedInterfaces';
import ProductColumn from './mergeProductColumn/MergeProductColumn';
import MergeFieldTitleColumn from './MergeFieldTitleColumn';

export type NumberOfColumnsOptions = 1 | 2 | 3 | 4 | 5 | 6 | 8 | 9 | 10;

interface Props {
    fieldsToShow: ProductData[];
    products: ProductInCatalog[];
    customProductDetailsHeader?: ReactNode;
    hideExactMatch: boolean;
    numberOfColumns?: NumberOfColumnsOptions;
    removeProduct: (productId: number) => void;
    resetMergeProduct: () => void;
    selectExistingProductToMergeTo: (existingProduct: ProductInCatalog) => void;
    startingProductIndex?: number;
}

export const MergeProductGrid: FunctionComponent<Props> = (props: Props) => {
    const {
        customProductDetailsHeader,
        fieldsToShow,
        hideExactMatch,
        numberOfColumns = 4,
        products,
        removeProduct,
        resetMergeProduct,
        selectExistingProductToMergeTo,
        startingProductIndex = 0,
    } = props;

    const [productsToRender, setProductsToRender] = useState<ProductInCatalog[]>([]);

    const useStyles = makeStyles({
        productGrid: {
            display: 'flex',
            width: '100%',
            overflowX: 'scroll',
            flexWrap: 'nowrap',
        },
    });

    const {productGrid} = useStyles();

    useEffect(() => {
        setProductsToRender(
            _.slice(products, startingProductIndex, startingProductIndex + numberOfColumns)
        );
    }, [products, startingProductIndex, numberOfColumns]);

    // if we have 2 products left to use for the merge we want to disable removal of anymore products
    // as we will not have any products left for the merge. Components utilizing this function further down in the component tree will check
    // to see if they can use this function and react accordingly.
    const removeProductFn = _.size(productsToRender) > 2 ? removeProduct : undefined;

    const columnWidth = _.floor(12 / numberOfColumns) as ColumnWidthOption;

    return (
        <Grid container item justifyContent="flex-end" className={productGrid} sm={12}>
            <Grid container item sm={2}>
                <MergeFieldTitleColumn
                    customHeaderCell={customProductDetailsHeader}
                    fieldsToShow={fieldsToShow}
                    hideExactMatch={hideExactMatch}
                    resetMergeProduct={resetMergeProduct}
                />
            </Grid>
            <Grid container item sm={10}>
                {_.map(productsToRender, (product, idx) => {
                    return (
                        <ProductColumn
                            columnWidth={columnWidth}
                            fieldsToShow={fieldsToShow}
                            key={`${product.id}-${idx}`}
                            mergeToThisProduct={selectExistingProductToMergeTo}
                            product={product}
                            removeProduct={removeProductFn}
                        />
                    );
                })}
            </Grid>
        </Grid>
    );
};

export default MergeProductGrid;
